import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { FileText, Copy, X, Code, Download } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import type { CommunicationTemplate } from "@/types/communication-template";

interface CommunicationTemplateCodeModalProps {
  template: CommunicationTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

export function CommunicationTemplateCodeModal({ template, isOpen, onClose }: CommunicationTemplateCodeModalProps) {
  const { toast } = useToast();

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard",
        description: "Template code has been copied to your clipboard.",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy to clipboard. Please select and copy manually.",
        variant: "destructive",
      });
    }
  };

  if (!template) return null;

  const codeContent = `Email Subject: ${template.email_subject_template || template.subject_template || 'N/A'}

Email Content Template:
${template.email_content_template || template.content_template || 'N/A'}

${template.sms_content_template ? `SMS Content Template:
${template.sms_content_template}

` : ''}${template.webhook_payload_template && Object.keys(template.webhook_payload_template).length > 0 ? `Webhook Payload Template:
${JSON.stringify(template.webhook_payload_template, null, 2)}

` : ''}Variables:
${JSON.stringify(template.variables, null, 2)}

Sample Data:
${JSON.stringify(template.sample_data, null, 2)}

Template Format: ${template.template_format || 'react_email'}
${template.react_email_template ? `React Component: ${template.react_email_template}` : ''}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              {template.name} - Template Code
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{template.template_format}</Badge>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 overflow-auto">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">{template.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Category: {template.category}
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(codeContent)}
                    className="flex items-center gap-2"
                  >
                    <Copy className="h-3 w-3" />
                    Copy
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="relative">
                <pre className="overflow-auto p-4 bg-muted rounded-lg text-sm font-mono max-h-[500px]">
                  <code>{codeContent}</code>
                </pre>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}